import React from "react";
import { withStyles } from "@material-ui/core/styles";
import DataGrid from '@dataGrid';
import OptimizationPickupsConfig from "./GridConfig";
import { observer, inject } from "mobx-react";
import { Grid } from "@material-ui/core";
import { PickupMap } from "../Map";
import { withSnackbar } from "notistack";
import { compose } from "recompose";
import StatusBar from '@dataGrid/components/StatusBar';
import { postData } from "../../../../../api/componentAPIs";
import MESSAGES from "../../../../../appMessages";
import { isPickupFiltered } from "../../../../modules/Dispatch/RouteExecution/filterUtils";
import { withFeatureFlags } from "../../../../../userProfile/featureFlagsContext";
import appConstants from "../../../../../appConstants";
import { PICKUP_OPTIMYZER_FILTERS } from "../constants";
import PickupCheckbox from "./PickupCheckbox";
import {
  isPartialAssignedPickupsSelected,
  isPartialUnassignedPickupsSelected,
  getAllUnassignedPickups,
  getAllAssignedPickups,
  isUnassignedPickupSelected,
  isPickupSelectedInGrid,
  isPendingAssignedPickupSelected,
  getDefaultFilters,
  getDefaultSelectedFilters,
} from "../utils";
import styles from "./styles";

@inject("DispatchStore")
@observer
class CorrectPickupInfo extends React.Component {
  constructor(props) {
    super(props);
    this.gridApi = {};
    this.state = {
      showPickupsList: props.showPickupsList,
      filters: getDefaultFilters(),
      selectedFilter: getDefaultSelectedFilters(),
    };
    this.gridConfig = new OptimizationPickupsConfig({
      userConfig: this.props.userData.userProfile?.columnPref,
      featureFlags: {
        IsWeightBasedRoutingEnabled: this.props.featureFlags.isFeatureEnabled(
          appConstants.features.isWeightBasedRouting
        ),
      },
    });
  }

  onGridReady = ({ api }) => {
    this.gridApi = api;
    this.filterPickupsList();
  };

  componentDidMount() {
    this.props.glEventHub.on(
      "pickupSelectedOnMapPopup",
      this.onPickupSelectedOnMap
    );
    const isAssigned = isPendingAssignedPickupSelected(
      this.props.DispatchStore.pickupListForOptimization,
      this.getSelectedPickupIdList()
    );
    const isUnassigned = this.getDefaultUnassignedValue();
    this.setState({
      selectedFilter: {
        isAssigned,
        isUnassigned: isUnassigned || !isAssigned,
      },
    });
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.state.selectedFilter.isAssigned !==
        prevState.selectedFilter.isAssigned ||
      this.state.selectedFilter.isUnassigned !==
        prevState.selectedFilter.isUnassigned
    ) {
      this.filterPickupsList();
    }

    // Handle selectedPickupsList changes from parent
    this.handlePickupSelectionChange(prevProps.selectedPickupsList);
  }

  componentWillUnmount() {
    this.props.glEventHub.off(
      "pickupSelectedOnMapPopup",
      this.onPickupSelectedOnMap
    );
  }

  handlePickupSelectionChange = (prevSelectedPickupsList) => {
    if (
      this.props.selectedPickupsList?.length &&
      JSON.stringify(prevSelectedPickupsList) !== JSON.stringify(this.props.selectedPickupsList)
    ) {
      this.selectPickups(
        this.props.selectedPickupsList.map((pickup) => pickup.pickupId)
      );
    }
  };

  getSelectedPickupIdList = () => {
    return this.props.selectedPickupsList.map(pickup=>pickup.pickupId.toLowerCase())
  }

  getDefaultUnassignedValue = () => {
    const { pickupListForOptimization } = this.props.DispatchStore;
    const selectedPickupIdList = this.getSelectedPickupIdList();
    return isPickupSelectedInGrid(selectedPickupIdList)
      ? isUnassignedPickupSelected(
        pickupListForOptimization,
        selectedPickupIdList
      )
      : true;
  };

  handleFilterChange = (event) => {
    event.persist();
    this.setState((prevState) => ({
      ...prevState,
      selectedFilter: {
        ...prevState.selectedFilter,
        [event.target.name]: event.target.checked,
      },
    }));
  };

  filterPickupsList = () => {
    const pickupsList = this.props.DispatchStore.pickupListForOptimization;
    const filters = this.state.filters.filter(
      (option) => this.state.selectedFilter[option.name]
    );
    const showPickupsList = filters.length
      ? pickupsList.filter((pickup) =>
          isPickupFiltered({
            pickup,
            filters,
            terminalOffset: this.props.DispatchStore.terminalOffset,
          })
        )
      : [];
    this.setState({ showPickupsList });
  };

  setSelectedPickups = (filters) => {
    const pickupsList = this.props.DispatchStore.pickupListForOptimization;
    const { selectedPickupsList } = this.props;
    let selectedAssignedPickups = [];
    let selectedUnassignedPickups = [];
    if (filters.includes(PICKUP_OPTIMYZER_FILTERS.assigned)) {
      if (isPartialAssignedPickupsSelected(selectedPickupsList, pickupsList)) {
        selectedAssignedPickups = getAllAssignedPickups(selectedPickupsList);
      } else {
        selectedAssignedPickups = getAllAssignedPickups(pickupsList);
      }
    }
    if (filters.includes(PICKUP_OPTIMYZER_FILTERS.unassigned)) {
      if (
        isPartialUnassignedPickupsSelected(selectedPickupsList, pickupsList)
      ) {
        selectedUnassignedPickups =
          getAllUnassignedPickups(selectedPickupsList);
      } else {
        selectedUnassignedPickups = getAllUnassignedPickups(pickupsList);
      }
    }
    this.selectPickups(
      [...selectedAssignedPickups, ...selectedUnassignedPickups].map(
        (pickup) => pickup.pickupId
      )
    );
  };

  onCellValueChanged = (rowValue) => {
    const field = rowValue.colDef.field;
    const value = rowValue.value;
    if (this.isUpdateValid({ field, value, currentCell: rowValue })) {
      let data;
      this.props.showPickupUpdateInProgress();
      if (field === "pse") {
        data = {
          terminalCode: this.props.userData.userProfile.terminal.code,
          pickups: [
            {
              pickupId: rowValue.data.pickupId,
              pickupReadyTime: rowValue.data.pickupReadyTime,
              pickupCloseTime: rowValue.data.pickupCloseTime,
              pse: rowValue.newValue,
            },
          ],
        };
      } else {
        const [startTime, endTime] =
          rowValue.newValue && rowValue.newValue.split("-");
        data = {
          terminalCode: this.props.userData.userProfile.terminal.code,
          pickups: [
            {
              pickupId: rowValue.data.pickupId,
              pickupReadyTime: startTime,
              pickupCloseTime: endTime,
              pse: rowValue.data.pse,
            },
          ],
        };
      }
      postData("UpdatePickupData", data).then(
        (response) => {
          if (response && response.status >= 200 && response.status < 400) {
            this.props.glEventHub.emit(
              "DispatchEvents",
              "RefreshDispatchStore",
              []
            );
            this.props.enqueueSnackbar(
              MESSAGES.OPTIMIZATION_PICKUP.UPDATE_SUCCESS,
              { variant: "success" }
            );
            this.props.hidePickupUpdateInProgress();
          } else {
            this.handleInvalidUpdate({field, rowValue, statusCode: response.status});
          }
        },
        () => {
          this.handleInvalidUpdate({field, rowValue});
        }
      );
    } else {
      this.restoreCellValue(field, rowValue);
    }
  };
  isUpdateValid = ({ field, value, currentCell }) => {
    let isUpdateValid = true;
    switch (field) {
      case "pse":
        if (value === "") {
          this.props.enqueueSnackbar(MESSAGES.OPTIMIZATION_PICKUP.PSE_EMPTY, {
            variant: "error",
          });
          isUpdateValid = false;
        } else if (value === 0) {
          this.props.enqueueSnackbar(
            MESSAGES.OPTIMIZATION_PICKUP.PSE_ZEROVALUE,
            {
              variant: "error",
            }
          );
          isUpdateValid = false;
        } else if (
          currentCell &&
          Number(currentCell.oldValue) === Number(value)
        ) {
          this.props.enqueueSnackbar(MESSAGES.COMMON.NO_CHANGES_TO_SAVE, {
            variant: "info",
          });
          isUpdateValid = false;
        }
        break;
      case "pickupWindow":
        if (!value) {
          this.props.enqueueSnackbar(
            MESSAGES.OPTIMIZATION_PICKUP.PICKUPWINDOW_EMPTY,
            { variant: "error" }
          );
          isUpdateValid = false;
        } else if (
          (value &&
            (value.includes(appConstants.INVALID_TIME_RANGE) || value.includes("undefined"))) ||
          value.split("-").length != 2
        ) {
          this.props.enqueueSnackbar(
            MESSAGES.OPTIMIZATION_PICKUP.PICKUPWINDOW_INVALID,
            { variant: "error" }
          );
          isUpdateValid = false;
        } else if (currentCell && currentCell.oldValue === value) {
          this.props.enqueueSnackbar(MESSAGES.COMMON.NO_CHANGES_TO_SAVE, {
            variant: "info",
          });
          isUpdateValid = false;
        }
        break;
    }
    return isUpdateValid;
  };
  restoreCellValue = (field, rowValue) => {
    if (field === "pickupWindow") {
      const [startTime, endTime] =
        rowValue.oldValue && rowValue.oldValue.split("-");
      rowValue.data["pickupReadyTime"] = startTime;
      rowValue.data["pickupCloseTime"] = endTime;
      this.gridApi.refreshCells({ columns: [field], force: true });
    } else {
      rowValue.data[field] = rowValue.oldValue;
      this.gridApi.refreshCells({ columns: [field], force: true });
    }
  };

  handleInvalidUpdate = ({field, rowValue, statusCode}) => {
    this.restoreCellValue(field, rowValue);
    this.props.hidePickupUpdateInProgress();
    if (statusCode === 403) {
      return;
    }
    this.props.enqueueSnackbar(
      MESSAGES.OPTIMIZATION_PICKUP.PICKUP_UPDATE_FAILURE,
      { variant: "error" }
    );
  };

  selectPickups = (pickupIdList) => {
    if (!this.gridApi?.forEachNode) {
      return;
    }
    this.gridApi.forEachNode((node) => {
      if (pickupIdList.includes(node.data.pickupId)) {
        node.setSelected(true, false); // isSelected, clearOtherSelection
        if (this.gridApi.ensureIndexVisible) {
          this.gridApi.ensureIndexVisible(node.rowIndex);
        }
      } else {
        node.setSelected(false, false);
      }
    });
  };

  constructStatusBar = (filteredData) => {
    return [
      {
        label: filteredData.length + " Total Pickups",
        filterCondition: "",
      },
      {
        label: this.props.selectedPickupsList.length + " Selected Pickups",
        filterCondition: "",
      },
    ];
  };

  onSelectionChanged = () => {
    if (this.gridApi) {
      const selectedPickupsList = this.gridApi.getSelectedRows()
        .map((pickup) => {
          return {
            pickupId: pickup.pickupId,
            position: {
              lat: pickup.shipperLatitude,
              lng: pickup.shipperLongitude,
            },
            ...pickup,
          };
        });
      this.props.glEventHub.emit(
        "pickupSelectedOnGridPopup",
        selectedPickupsList
      );
      this.props.setSelectedPickups(selectedPickupsList);
    }
  };

  onPickupSelectedOnMap = (pickups) => {
    if (this.gridApi) {
      const pickupidList = pickups.map((pickup) => {
        return pickup.pickupId;
      });
      this.gridApi.forEachNode((node) => {
        if (pickupidList.includes(node.data.pickupId)) {
          node.setSelected(true, false);
          this.gridApi.ensureIndexVisible(node.rowIndex);
        } else {
          node.setSelected(false, false);
        }
      });
    }
  };

  onRowDataUpdated = () => {
    const activeFilters = this.state.filters
      .filter((option) => this.state.selectedFilter[option.name])
      .map((filter) => filter.label);
    if (activeFilters.length) {
      this.setSelectedPickups(activeFilters);
    }
  };

  render() {
    const { isAssigned, isUnassigned } = this.state.selectedFilter;
    const { classes } = this.props;
    const pickupsList = this.props.DispatchStore.pickupListForOptimization;
    const driverInfoList =
      this.state.showPickupsList.length > 0 ? this.state.showPickupsList : [];
    return (
      <Grid container className={classes.container}>
        <Grid item xs={4} className={classes.topGridContainer}>
          <PickupCheckbox
            isPartialUnassignedPickupsSelected={isPartialUnassignedPickupsSelected(
              this.props.selectedPickupsList,
              pickupsList
            )}
            isPartialAssignedPickupsSelected={isPartialAssignedPickupsSelected(
              this.props.selectedPickupsList,
              pickupsList
            )}
            isAssigned={isAssigned}
            isUnassigned={isUnassigned}
            handleFilterChange={this.handleFilterChange}
          />
          <div className={classes.bottomGridContainer}>
            <div className={classes.headerStyle}>Pickups for Optimization</div>
            <div className={classes.dataGrid}>
              <DataGrid
                defaultColDef={this.gridConfig.config.defaultColDef}
                columnDefs={this.gridConfig.config.columns}
                columnTypes={this.gridConfig.config.columnTypes}
                config={this.gridConfig.config}
                onGridReady={this.onGridReady}
                rowSelection={this.gridConfig.config.rowSelection}
                rowDeselection={this.gridConfig.config.rowDeselection}
                rowData={driverInfoList}
                onSelectionChanged={this.onSelectionChanged}
                onRowDataUpdated={this.onRowDataUpdated}
                rowHeight={65}
                suppressRowClickSelection
                onCellValueChanged={this.onCellValueChanged}
                panelLabel={(filteredData) => (
                  <StatusBar data={this.constructStatusBar(filteredData)} />
                )}
              />
            </div>
          </div>
        </Grid>
        <Grid item xs={8} className={classes.mapContainer}>
          <PickupMap
            pickupList={driverInfoList}
            glEventHub={this.props.glEventHub}
            userData={this.props.userData}
          />
        </Grid>
      </Grid>
    );
  }
}

export default compose(
  withStyles(styles),
  withSnackbar,
  withFeatureFlags
)(CorrectPickupInfo);
